export const REPLY_AUDIO_GAIN_STORAGE_KEY =
  "formula-ae-ios-reply-audio-gain-db-v3";
export const DEFAULT_REPLY_AUDIO_GAIN_DB = -2;
export const MIN_REPLY_AUDIO_GAIN_DB = -12;
export const MAX_REPLY_AUDIO_GAIN_DB = 6;
export const REPLY_AUDIO_GAIN_RAMP_SECONDS = 0.1;

type WebkitWindow = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
};

export function normalizeReplyAudioGainDb(value: unknown): number {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_REPLY_AUDIO_GAIN_DB;
  return Math.min(
    MAX_REPLY_AUDIO_GAIN_DB,
    Math.max(MIN_REPLY_AUDIO_GAIN_DB, Math.round(parsed)),
  );
}

export function decibelsToReplyGain(decibels: number): number {
  return 10 ** (normalizeReplyAudioGainDb(decibels) / 20);
}

export function loadReplyAudioGainDb(storage: Storage | null): number {
  if (!storage) return DEFAULT_REPLY_AUDIO_GAIN_DB;
  try {
    const stored = storage.getItem(REPLY_AUDIO_GAIN_STORAGE_KEY);
    return stored === null
      ? DEFAULT_REPLY_AUDIO_GAIN_DB
      : normalizeReplyAudioGainDb(stored);
  } catch {
    return DEFAULT_REPLY_AUDIO_GAIN_DB;
  }
}

export function saveReplyAudioGainDb(storage: Storage | null, decibels: number): void {
  if (!storage) return;
  try {
    storage.setItem(
      REPLY_AUDIO_GAIN_STORAGE_KEY,
      String(normalizeReplyAudioGainDb(decibels)),
    );
  } catch {
    // Private browsing and managed browsers can deny persistent storage.
  }
}

export class IosReplyAudioGainController {
  private audioContext: AudioContext | null = null;
  private source: MediaElementAudioSourceNode | null = null;
  private gain: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private muteGate: GainNode | null = null;
  private gainDb = DEFAULT_REPLY_AUDIO_GAIN_DB;
  private muted = false;

  enableFromUserGesture(element: HTMLAudioElement): void {
    this.ensureAudioPath(element);
    void this.audioContext?.resume().catch(() => {
      // The same element is primed again by a later trusted gesture if WebKit
      // suspends its audio context.
    });
    this.applyGain(false);
    this.applyMute();
  }

  setGainDb(decibels: number): void {
    this.gainDb = normalizeReplyAudioGainDb(decibels);
    this.applyGain(true);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyMute();
  }

  close(): void {
    this.source?.disconnect();
    this.gain?.disconnect();
    this.limiter?.disconnect();
    this.muteGate?.disconnect();
    void this.audioContext?.close().catch(() => {});
    this.source = null;
    this.gain = null;
    this.limiter = null;
    this.muteGate = null;
    this.audioContext = null;
  }

  private ensureAudioPath(element: HTMLAudioElement): void {
    if (this.gain || this.audioContext) return;
    const AudioContextConstructor = window.AudioContext
      ?? (window as WebkitWindow).webkitAudioContext;
    if (!AudioContextConstructor) return;
    let audioContext: AudioContext | null = null;
    let source: MediaElementAudioSourceNode | null = null;
    try {
      audioContext = new AudioContextConstructor();
      source = audioContext.createMediaElementSource(element);
      const gain = audioContext.createGain();
      const limiter = audioContext.createDynamicsCompressor();
      const muteGate = audioContext.createGain();
      const now = audioContext.currentTime;
      limiter.threshold.setValueAtTime(-1, now);
      limiter.knee.setValueAtTime(1, now);
      limiter.ratio.setValueAtTime(12, now);
      limiter.attack.setValueAtTime(0.003, now);
      limiter.release.setValueAtTime(0.15, now);
      source.connect(gain);
      gain.connect(limiter);
      limiter.connect(muteGate);
      muteGate.connect(audioContext.destination);
      this.audioContext = audioContext;
      this.source = source;
      this.gain = gain;
      this.limiter = limiter;
      this.muteGate = muteGate;
    } catch {
      if (audioContext && source) {
        try {
          source.connect(audioContext.destination);
          this.audioContext = audioContext;
          this.source = source;
        } catch {
          void audioContext.close().catch(() => {});
        }
      } else {
        void audioContext?.close().catch(() => {});
      }
      this.gain = null;
      this.limiter = null;
      this.muteGate = null;
    }
  }

  private applyGain(smooth: boolean): void {
    if (!this.gain || !this.audioContext) return;
    const target = decibelsToReplyGain(this.gainDb);
    const parameter = this.gain.gain;
    const now = this.audioContext.currentTime;
    parameter.cancelScheduledValues(now);
    parameter.setValueAtTime(parameter.value, now);
    if (smooth) {
      parameter.linearRampToValueAtTime(target, now + REPLY_AUDIO_GAIN_RAMP_SECONDS);
    } else {
      parameter.setValueAtTime(target, now);
    }
  }

  private applyMute(): void {
    if (!this.muteGate || !this.audioContext) return;
    const parameter = this.muteGate.gain;
    const now = this.audioContext.currentTime;
    parameter.cancelScheduledValues(now);
    parameter.setValueAtTime(this.muted ? 0 : 1, now);
  }
}
